/* eslint react-hooks/exhaustive-deps: "warn" */

import { useContext, useRef } from 'react';

import cn from 'classnames';

import { ContentContext } from 'context/ContentContext';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Button } from 'ui/atoms/Button/Button';
import { Faq } from 'ui/atoms/Faq/Faq';
import { AlertRoutingBoard } from 'ui/molecules/AlertRoutingBoard/AlertRoutingBoard';
import { CompareMatrix } from 'ui/molecules/CompareMatrix/CompareMatrix';
import { EditorialStepsSection } from 'ui/molecules/EditorialStepsSection/EditorialStepsSection';
import { HeroSection } from 'ui/molecules/HeroSection/HeroSection';
import { InfiniteScroller } from 'ui/molecules/InfiniteScroller/InfiniteScroller';
import { PerpetualFeedCard } from 'ui/molecules/PerpetualFeedCard/PerpetualFeedCard';
import { Section } from 'ui/molecules/Section/Section';
import { AccessRoutes } from 'ui/organisms/AccessRoutes/AccessRoutes';
import { CtaSection } from 'ui/organisms/CtaSection/CtaSection';

import styles from './ContinuousMonitoringPage.module.scss';

import {
  getChangeTypeChips,
  getCompareColumns,
  getCompareRows,
  getFaqItems,
  getFeedAlerts,
  getMonitorSteps,
  getPerpetualLoopSteps,
  getPricingColumns,
  getRoutingDestinations,
} from './ContinuousMonitoringPage.data';

export const ContinuousMonitoringPage = () => {
  const { secondaryNavigation } = useContext(ContentContext);
  const { t } = useInt();

  const priceBandRef = useRef<HTMLDivElement>(null);
  const priceBandVisible = useIntersection(priceBandRef, { threshold: 0.2 });

  const freeTrialSlug = secondaryNavigation?.free_trial?.slug;
  const bookDemoSlug = secondaryNavigation?.book_demo?.slug;

  const chipItems = getChangeTypeChips(t).map((chip) => (
    <span key={chip} className={styles.chip}>
      {chip}
    </span>
  ));

  const scanCta = freeTrialSlug && (
    <Button type="blue" size="xxl" route={freeTrialSlug}>
      {t('Run a free portfolio scan')}
    </Button>
  );

  return (
    <div className={styles.wrapper}>
      <HeroSection
        align="left"
        title={t('Monitor every company in your portfolio. Perpetually.')}
        description={
          <>
            {t('Perpetual KYB across 400+ government registries. Track changes to')}{' '}
            <strong>{t('ownership, directors, status, and filed accounts')}</strong>{' '}
            {t(
              'for every company you work with. Updates delivered the same day a filing is made. 600M+ companies, 200+ countries.',
            )}
          </>
        }
        actions={scanCta}
        media={
          <PerpetualFeedCard
            alerts={getFeedAlerts(t)}
            monitoredCount="4,988"
            footerNote={t('400+ registries')}
            className={styles.heroFeed}
          />
        }
        mediaPosition="right"
        className={styles.hero}
      />

      <div className={styles.trusted}>
        <p className={styles.trustedLabel}>{t('38 change types · 400+ registries')}</p>

        <InfiniteScroller
          items={chipItems}
          className={styles.chipMarquee}
          slideWidth={150}
          slideGap={10}
          animationDuration={55}
        />
      </div>

      <Section
        align="left"
        title={t('Periodic checks once in a while. Perpetual catches it as it happens.')}
        description={t(
          "The difference isn't a feature — it's the whole model. Here's how the two approaches compare on the things a compliance team actually cares about.",
        )}
      >
        <CompareMatrix columns={getCompareColumns(t)} rows={getCompareRows(t)} className={styles.compare} />
      </Section>

      {freeTrialSlug && (
        <CtaSection
          layout="split"
          theme="dark"
          title={t('Find out what you already missed.')}
          description={t(
            'Send up to 50 companies as a CSV or a pasted list. We match them against 400+ government registries and return every filing made since your last review.',
          )}
          actions={
            <Button type="white" size="xl" route={freeTrialSlug}>
              {t('Run a free portfolio scan')}
            </Button>
          }
        />
      )}

      <EditorialStepsSection
        background="blue"
        title={t('Every registry event that changes your risk.')}
        description={t(
          'Tracked straight from the primary government record — the source itself, not a copy of a copy. Sourced directly from 400+ government registries across 200+ countries.',
        )}
        steps={getMonitorSteps(t)}
      />

      <Section
        theme="dark"
        align="left"
        background="#0e1526"
        extra={t('One alert. Five ways in.')}
        title={t('How you get the alerts.')}
        description={t(
          'One filing detected at the registry, routed to every system your team works in — the same day.',
        )}
        className={styles.deliverySection}
      >
        <AlertRoutingBoard
          sourceTitle={t('Registry filing detected')}
          sourceMeta={t('400+ registries · same day')}
          destinations={getRoutingDestinations(t)}
        />
      </Section>

      <AccessRoutes
        background="light"
        numbered
        heading={t('How Global Database ensures perpetual updates.')}
        lede={t(
          'Five steps, running every day across 400+ government registries. Nothing to schedule, nothing to trigger.',
        )}
        routes={getPerpetualLoopSteps(t)}
      />

      <Section
        align="left"
        title={t('One price per company. Unlimited updates for twelve months.')}
        description={t(
          'Periodic verification charges per lookup — and most lookups return no change. Perpetual pricing charges per relationship, so you only pay for coverage, never for checking.',
        )}
      >
        <div ref={priceBandRef} className={cn(styles.priceBand, priceBandVisible && styles.priceBandIn)}>
          <span className={styles.priceGlow} aria-hidden="true" />

          {getPricingColumns(t).map((column) => (
            <div key={column.label} className={styles.priceCol}>
              <span className={styles.priceBadgeSlot}>
                {column.highlight && <span className={styles.priceBadge}>{t('Recommended')}</span>}
              </span>

              <h4 className={styles.priceLabel}>{column.label}</h4>

              <p className={cn(styles.priceValue, column.highlight && styles.priceValueHighlight)}>{column.value}</p>

              <p className={styles.priceText}>{column.description}</p>
            </div>
          ))}
        </div>
      </Section>

      <CtaSection
        theme="light"
        title={t('Your portfolio changed today. Did you notice?')}
        description={t(
          "Send us your portfolio and see what's already changed since your last review. Most teams find material events in the first scan.",
        )}
        actions={
          <>
            {freeTrialSlug && (
              <Button type="blue" size="xxl" route={freeTrialSlug}>
                {t('Run a free portfolio scan')}
              </Button>
            )}

            {bookDemoSlug && (
              <Button type="dark-outline" size="xxl" route={bookDemoSlug}>
                {t('Book a demo')}
              </Button>
            )}
          </>
        }
      />

      <Faq title={t('Perpetual KYB, answered.')} data={getFaqItems(t)} />
    </div>
  );
};
